import { UATConstants } from 'src/Common/Models/UATConstants';
import { ITenantUIConfig, TenantProvider } from 'src/tenantConfig/interface/index.ts';
import { MarketplaceFilterConfig } from './filters/MarketplaceFilterConfig';

/**
 * UI tenant config for the "Microsoft Marketplace" tenant (recordType: MarketplaceRequest).
 *
 * Modeled on the server-driven, Artemis-backed pattern (MCAPS Leader RoB) with the minimal
 * Hub/ManageView shape used by Compete. The create form itself is owned by the parallel
 * server-driven form session; this file only makes the tenant selectable and surfaces its
 * Create menu item.
 *
 * TODOs pending backend/onboarding inputs (kept intentionally minimal so nothing renders broken):
 * - nodeName: confirm the Marketplace hierarchy node string used by the backend/Artemis.
 * - provider: ARTEMIS chosen because the server submission sink sets artemisCall = true.
 * - ManageView view-folder GUIDs (defaultViewId/systemViewsFolderId/sharedViewsFolderId): wired to
 *   MOCK ids (see marketplace-views.json); replace with backend-allocated ids once the Artemis view
 *   folders are provisioned (still pending — the real ids are allocated by the Artemis view store).
 * - Hub.FilterConfig / SelectableColumns: bound to `MarketplaceFilterConfig` (§2.1). The standard RAID
 *   column paths are verified; the Marketplace-specific ones are provisional until the indexed
 *   RAIDEntity schema is confirmed — see the caveat in `MarketplaceFilterConfig.ts`.
 */
export const MarketplaceTenantConfig: Partial<ITenantUIConfig> = {
  nodeName: ['Microsoft Marketplace'],
  provider: TenantProvider.ARTEMIS,
  TenantExperience: true,
  // TODO: hardcoded to disable the server-driven MANAGE (edit) form for now; falls back to the legacy
  // MarketplaceForm. Remove once the server-driven manage form (GET forms/{id} + PATCH) is ready.
  disableServerDrivenManageForm: true,
  FeatureConfig: {
    SensitiveInfoValidation: false,
    SensativeInfoErrorCount: false,
    FieldConfig: MarketplaceFilterConfig,
    AttachmentConfigurations: [],
    Navigation: {
      GlobalSearch: true,
      Copilot: true,
      Analytics: false,
      RequestAccess: true,
      Settings: true,
      Notifications: true,
      Help: true,
    },
    Hub: {
      Filter: true,
      FilterConfig: MarketplaceFilterConfig,
      AreaPath: [],
      Title: UATConstants.TenantTitles.Marketplace,
      ADOTitle: UATConstants.TenantADOText.Marketplace,
      Create: [
        {
          Text: UATConstants.CreateActionMenuTitle.CreateMarketplaceRequest,
          Key: UATConstants.CreateActionMenuKey.CreateMarketplaceRequest,
          NodeName: '',
        },
      ],
      Download: true,
      RelatedItem: true,
      Email: true,
      LocalSearch: true,
      ColumnOptions: true,
      ColumnOptionsConfig: [],
      SplitMode: true,
      BulkEdit: false,
      BulkEditConfig: [],
      CreateRAID: false,
      // Selectable columns = the filter fields' RAID paths (the Artemis "filterable == selectable"
      // model, mirroring `filterableAndSelectableColumnsROB`). Derived from `MarketplaceFilterConfig`
      // so the two never drift.
      SelectableColumns: MarketplaceFilterConfig.map((field) => field.internalName).filter(
        (name): name is string => Boolean(name)
      ),
    },
    ManageAction: {
      NewExperience: true,
      MandateFormPickers: [
        { formFieldName: 'assignedto', formFieldLabel: UATConstants.PlaceholdersAndTitles.ActionOwner, apiFieldName: 'assignedto' },
        { formFieldName: 'requestor', formFieldLabel: UATConstants.PlaceholdersAndTitles.Requestor, apiFieldName: 'requestorsinfo' },
      ],
      Header: {
        Tags: true,
        Actions: {
          Follow: true,
          Email: true,
          CopyLink: true,
        },
      },
      Tabs: {
        TechnicalFeedback: false,
        RelatedItems: {
          showRelatedTab: true,
          showTFTCreated: false,
        },
        Attachments: true,
        History: true,
      },
      Panes: {
        CopilotInsights: {
          CustomerInsights: false,
          DiscussionInsights: false,
        },
        StatusUpdate: true,
        ClosureStatement: true,
        Discussion: true,
        Tasks: false,
        EmailConversation: false,
      },
    },
    ManageView: {
      isArtemisIntegrationEnabled: true,
      showAddViewButton: true,
      showAddFolderButton: true,
      showCommandBar: true,
      showPreviewFeatureMessageBar: false,
      showSelectFields: false,
      systemViewsFolderId: 'a1f0c2d3-1111-4a2b-9c3d-000000000001',
      sharedViewsFolderId: 'a1f0c2d3-2222-4a2b-9c3d-000000000002',
      dropdownValues: {},
    },
  },
};

// Generated by GitHub Copilot | Updated on: 2026-07-08 | 104 lines added | New Microsoft Marketplace UI tenant config (onboarding plumbing; server-driven form owned separately)
// Generated by GitHub Copilot | Updated on: 2026-07-08 | 5 lines added | Wired mock ManageView view-folder GUIDs (defaultViewId/systemViewsFolderId/sharedViewsFolderId) from marketplace-views.json
// Generated by GitHub Copilot | Updated on: 2026-08-18 | 3 lines removed | Removed bypassFlightCheck override; Marketplace tenant is now always available (not flight-gated)
